import { AuthFetch, WalletClient } from '@bsv/sdk'

let client: AuthFetch | null = null

export type DashboardFetch = (input: string, init?: RequestInit) => Promise<Response>

function authClient(): AuthFetch {
  if (!client) client = new AuthFetch(new WalletClient('auto'))
  return client
}

export function csrfFromCookie(cookie: string): string | undefined {
  const encoded = cookie.split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('proofrun_csrf='))
    ?.split('=')
    .slice(1)
    .join('=')
  return encoded ? decodeURIComponent(encoded) : undefined
}

export async function authenticatedDashboardFetch(
  url: string,
  options: RequestInit,
  cookie: string,
  nativeFetch: DashboardFetch,
  walletFetch: DashboardFetch,
): Promise<Response> {
  // GitHub OAuth sessions carry the readable, same-site CSRF cookie paired
  // with their HTTP-only session cookie. Keep those requests on the browser's
  // credentialed fetch transport. A wallet session has no such cookie and
  // continues through BRC-100 AuthFetch.
  if (csrfFromCookie(cookie)) return nativeFetch(url, {...options, credentials: 'same-origin'})
  return walletFetch(url, options)
}

export async function apiResponse(path: string, options?: RequestInit): Promise<Response> {
  const headers = new Headers(options?.headers)
  headers.set('content-type', 'application/json')
  const csrf = csrfFromCookie(document.cookie)
  if (csrf && !['GET', 'HEAD'].includes(String(options?.method || 'GET').toUpperCase())) headers.set('x-proofrun-csrf', csrf)
  const url = new URL(path, window.location.origin).toString()
  const request = {...options, headers: Object.fromEntries(headers.entries())}
  const response = await authenticatedDashboardFetch(
    url,
    request,
    document.cookie,
    (input, init) => fetch(input, init),
    (input, init) => authClient().fetch(input, init as Parameters<AuthFetch['fetch']>[1]),
  )
  if (!response.ok) throw new Error((await response.clone().json().catch(() => null))?.error || `Request failed (${response.status})`)
  return response
}

export async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await apiResponse(path, options)
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function sessionApi<T>(): Promise<T> {
  const response = await fetch('/v1/app/session', {credentials: 'same-origin', headers: {accept: 'application/json'}})
  if (!response.ok) throw new Error('authentication required')
  return response.json() as Promise<T>
}

export type Session = {
  identityKey: string
  displayName?: string
  authProvider: 'brc100' | 'github'
  memberships: Array<{organization_id: string, slug: string, name: string, plan: string, subscription_status: string, role: string}>
  projects: Array<{id: string, organization_id: string, slug: string, name: string, github_repository?: string}>
}

export type ProjectData = {
  project: {id: string, organization_id: string, name: string, slug: string, role: string, plan: string, monthly_case_limit: number, monthly_cost_limit_usd: number, organization_monthly_cost_limit_usd: number, evidence_retention_days: number, github_repository?: string}
  runs: Array<{id: string, status: string, verdict?: string, source: Record<string, unknown>, queued_at: string, remediation_eligible: boolean, budget_reserved_usd: number, actual_cost_usd: number, provider_calls: number}>
  profiles: Array<{id: string, profile_id: string, version: string, name: string, document: any, created_at: string}>
  devices: Array<{id: string, device_id: string, version: string, name: string, document: any}>
  workflows: Array<{id: string, workflow_id: string, version: string, digest: string, created_at: string}>
  runners: Array<{id: string, name: string, capabilities: string[], enabled: boolean, last_seen_at?: string, created_at?: string}>
  tokens: Array<{id: string, name: string, scopes: string[], created_at: string, last_used_at?: string, revoked_at?: string}>
  usage: {cases: number, cost_usd: number, reserved_cost_usd: number, provider_calls: number, input_tokens: number, output_tokens: number}
  billing: {plan: string, subscription_status: string, billing_mode: string, available_milli: number, reserved_milli: number, current_period_end?: string, next_credit_expiry?: string}
}
